import { createPortal } from "react-dom";

interface PlaceOptionsMenuProps {
  place: any;
  showArchivedPlaces: boolean;
  onClose: () => void;
  onUnarchive: () => void;
  onDelete: () => void;
  onAddToCollection: () => void;
  onArchive: () => void;
}

/**
 * The per-row "..." options menu for a place, portaled to document.body and
 * centered on screen. Each action's mutation and the menu-close side effect
 * (plus the window.confirm guard for permanent delete) stay in the orchestrator
 * via the callbacks.
 */
export function PlaceOptionsMenu({
  place,
  showArchivedPlaces,
  onClose,
  onUnarchive,
  onDelete,
  onAddToCollection,
  onArchive,
}: PlaceOptionsMenuProps) {
  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          background: "var(--color-surface)",
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          boxShadow: "var(--popover-shadow, 0 8px 32px rgba(0,0,0,0.15))",
          padding: 6,
          minWidth: 220,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: "8px 12px 6px",
            fontSize: 12,
            fontWeight: 600,
            color: "var(--color-text-faint)",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {place.title || place.name || "Place"}
        </div>
        {showArchivedPlaces ? (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onUnarchive();
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                padding: "10px 12px",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: 14,
                color: "var(--color-primary, #2a7d6e)",
                borderRadius: 6,
                textAlign: "left",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="21 8 21 21 3 21 3 8" />
                <rect x="1" y="3" width="22" height="5" />
                <line x1="10" y1="12" x2="14" y2="12" />
              </svg>
              Unarchive
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                padding: "10px 12px",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: 14,
                color: "#dc2626",
                borderRadius: 6,
                textAlign: "left",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#fef2f2")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              Delete
            </button>
          </>
        ) : (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAddToCollection();
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                padding: "10px 12px",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: 14,
                color: "var(--color-text-muted)",
                borderRadius: 6,
                textAlign: "left",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              </svg>
              Add to Collection
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onArchive();
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                padding: "10px 12px",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: 14,
                color: "#dc2626",
                borderRadius: 6,
                textAlign: "left",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#fef2f2")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              Archive
            </button>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
